// src/pages/AssignmentPanel.jsx
import React, { useState, useEffect } from 'react';
import {
  getClients,
  getUnassignedCountries,
  assignCountryToClient,
  getUnassignedDestinations,
  getUnassignedActivities,
  assignDestination,
  assignActivity,
  getAllDestinations,
  getAllActivities,
  getAllCountries
} from '../api';
import {
  Globe,
  MapPin,
  Activity,
  Check,
  RefreshCw,
  Search,
  Filter,
  Loader2,
  AlertCircle,
  Eye,
  EyeOff
} from 'lucide-react';

const AssignmentPanel = () => {
  const [activeTab, setActiveTab] = useState('countries');
  const [clients, setClients] = useState([]);
  
  // Unassigned data
  const [unassignedCountries, setUnassignedCountries] = useState([]);
  const [unassignedDestinations, setUnassignedDestinations] = useState([]);
  const [unassignedActivities, setUnassignedActivities] = useState([]);
  
  // All data with client info
  const [allDestinations, setAllDestinations] = useState([]);
  const [allActivities, setAllActivities] = useState([]);
  const [allCountries, setAllCountries] = useState([]);
  
  const [loading, setLoading] = useState(false);
  const [loadingStates, setLoadingStates] = useState({
    clients: false,
    countries: false,
    destinations: false,
    activities: false
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedItem, setSelectedItem] = useState('');
  const [error, setError] = useState('');
  const [showAssigned, setShowAssigned] = useState(true);

  useEffect(() => {
    console.log('🔄 Component mounted');
    loadAllData();
  }, []);

  // Debug state changes
  useEffect(() => {
    console.log('📊 State updated:', {
      activeTab,
      unassignedCountries: unassignedCountries.length,
      unassignedDestinations: unassignedDestinations.length,
      unassignedActivities: unassignedActivities.length,
      allCountries: allCountries.length,
      allDestinations: allDestinations.length,
      allActivities: allActivities.length
    });
  }, [unassignedCountries, unassignedDestinations, unassignedActivities, allCountries, allDestinations, allActivities, activeTab]);

  const loadAllData = async () => {
    setLoading(true);
    setError('');
    try {
      console.log('🔄 Loading all data...');
      
      // Load all data in parallel
      await Promise.all([
        loadClients(),
        loadUnassignedCountries(),
        loadUnassignedDestinations(),
        loadUnassignedActivities(),
        loadAllDestinations(),
        loadAllActivities(),
        loadAllCountries()
      ]);
      
      console.log('✅ All data loaded successfully');
      
    } catch (err) {
      console.error('❌ Error loading data:', err);
      setError('Failed to load data. Please refresh.');
    } finally {
      setLoading(false);
    }
  };

  const loadClients = async () => {
    setLoadingStates(prev => ({ ...prev, clients: true }));
    try {
      const res = await getClients();
      console.log('📦 Clients API response:', res);
      const data = res?.data || [];
      setClients(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading clients:', err);
      setClients([]);
    } finally {
      setLoadingStates(prev => ({ ...prev, clients: false }));
    }
  };

  const loadUnassignedCountries = async () => {
    setLoadingStates(prev => ({ ...prev, countries: true }));
    try {
      const res = await getUnassignedCountries();
      console.log('🌍 Unassigned countries API response:', res);
      const data = res?.data || [];
      setUnassignedCountries(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading unassigned countries:', err);
      setUnassignedCountries([]);
    } finally {
      setLoadingStates(prev => ({ ...prev, countries: false }));
    }
  };

  const loadUnassignedDestinations = async () => {
    setLoadingStates(prev => ({ ...prev, destinations: true }));
    try {
      const res = await getUnassignedDestinations();
      console.log('📍 Unassigned destinations API response:', res);
      const data = res?.data || [];
      setUnassignedDestinations(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading unassigned destinations:', err);
      setUnassignedDestinations([]);
    } finally {
      setLoadingStates(prev => ({ ...prev, destinations: false }));
    }
  };

  const loadUnassignedActivities = async () => {
    setLoadingStates(prev => ({ ...prev, activities: true }));
    try {
      const res = await getUnassignedActivities();
      console.log('🎯 Unassigned activities API response:', res);
      const data = res?.data || [];
      setUnassignedActivities(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading unassigned activities:', err);
      setUnassignedActivities([]);
    } finally {
      setLoadingStates(prev => ({ ...prev, activities: false }));
    }
  };

  const loadAllDestinations = async () => {
    try {
      const res = await getAllDestinations();
      console.log('🗺️ All destinations API response:', res);
      const data = res?.data || [];
      setAllDestinations(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading all destinations:', err);
      setAllDestinations([]);
    }
  };

  const loadAllActivities = async () => {
    try {
      const res = await getAllActivities();
      console.log('🎪 All activities API response:', res);
      const data = res?.data || [];
      setAllActivities(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading all activities:', err);
      setAllActivities([]);
    }
  };

  const loadAllCountries = async () => {
    try {
      const res = await getAllCountries();
      console.log('🌍 All countries API response:', res);
      const data = res?.data || [];
      setAllCountries(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading all countries:', err);
      setAllCountries([]);
    }
  };

  const handleAssign = async () => {
    if (!selectedClient || !selectedItem) {
      alert('Please select both client and item');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      let response;
      let success = false;
      
      if (activeTab === 'countries') {
        response = await assignCountryToClient({
          client_id: selectedClient,
          country_id: selectedItem
        });
        success = response?.status;
      } else if (activeTab === 'destinations') {
        response = await assignDestination({
          client_id: selectedClient,
          destination_id: selectedItem
        });
        success = response?.status;
      } else if (activeTab === 'activities') {
        response = await assignActivity({
          client_id: selectedClient,
          activity_id: selectedItem
        });
        success = response?.status;
      }

      if (success) {
        alert('✅ Assigned successfully!');
        setSelectedClient('');
        setSelectedItem('');
        await loadAllData(); // Reload all data
      } else {
        setError(response?.message || 'Assignment failed');
      }
    } catch (err) {
      console.error('Assignment error:', err);
      setError('Assignment failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getUnassignedList = () => {
    let list = [];
    switch(activeTab) {
      case 'countries':
        list = unassignedCountries;
        break;
      case 'destinations':
        list = unassignedDestinations;
        break;
      case 'activities':
        list = unassignedActivities;
        break;
      default:
        list = [];
    }
    console.log(`📋 getUnassignedList for ${activeTab}:`, list.length, 'items');
    return list;
  };

  const getAllList = () => {
    let list = [];
    switch(activeTab) {
      case 'countries':
        list = allCountries;
        break;
      case 'destinations':
        list = allDestinations;
        break;
      case 'activities':
        list = allActivities;
        break;
      default:
        list = [];
    }
    console.log(`📋 getAllList for ${activeTab}:`, list.length, 'items');
    return list;
  };

  const getLoadingState = () => {
    switch(activeTab) {
      case 'countries':
        return loadingStates.countries;
      case 'destinations':
        return loadingStates.destinations;
      case 'activities':
        return loadingStates.activities;
      default:
        return false;
    }
  };

  const filteredUnassigned = getUnassignedList().filter(item => {
    if (!item) return false;
    const searchLower = searchTerm.toLowerCase();
    return (
      (item.country_name && item.country_name.toLowerCase().includes(searchLower)) ||
      (item.destination_name && item.destination_name.toLowerCase().includes(searchLower)) ||
      (item.activity_name && item.activity_name.toLowerCase().includes(searchLower))
    );
  });

  const filteredAll = getAllList().filter(item => {
    if (!item) return false;
    const searchLower = searchTerm.toLowerCase();
    return (
      (item.country_name && item.country_name.toLowerCase().includes(searchLower)) ||
      (item.destination_name && item.destination_name.toLowerCase().includes(searchLower)) ||
      (item.activity_name && item.activity_name.toLowerCase().includes(searchLower)) ||
      (item.client_name && item.client_name.toLowerCase().includes(searchLower))
    );
  });

  const renderItemName = (item) => {
    if (!item) return '';
    switch(activeTab) {
      case 'countries':
        return item.country_name || '';
      case 'destinations':
        return item.destination_name || '';
      case 'activities':
        return item.activity_name || '';
      default:
        return '';
    }
  };

  const renderItemDetails = (item) => {
    if (!item) return null;
    switch(activeTab) {
      case 'destinations':
        return item.country_name ? <p className="text-sm text-gray-500">{item.country_name}</p> : null;
      case 'activities':
        return (
          <p className="text-sm text-gray-500">
            {item.destination_name || ''} {item.category_name ? `• ${item.category_name}` : ''}
          </p>
        );
      default:
        return null;
    }
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
          <Filter className="w-8 h-8 text-amber-500" />
          Assignment Panel
        </h1>
        <p className="text-gray-500 mt-2">Assign countries, destinations, and activities to clients</p>
      </div>

      

      {/* Error Display */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-600 rounded-xl flex items-center gap-3">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-6 bg-white p-2 rounded-2xl shadow-sm inline-flex">
        <button
          onClick={() => setActiveTab('countries')}
          className={`px-6 py-3 rounded-xl font-bold flex items-center gap-2 transition-all ${
            activeTab === 'countries' 
              ? 'bg-amber-500 text-white' 
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <Globe size={20} />
          Countries ({unassignedCountries.length} unassigned)
        </button>
        <button
          onClick={() => setActiveTab('destinations')}
          className={`px-6 py-3 rounded-xl font-bold flex items-center gap-2 transition-all ${
            activeTab === 'destinations' 
              ? 'bg-amber-500 text-white' 
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <MapPin size={20} />
          Destinations ({unassignedDestinations.length} unassigned)
        </button>
        <button
          onClick={() => setActiveTab('activities')}
          className={`px-6 py-3 rounded-xl font-bold flex items-center gap-2 transition-all ${
            activeTab === 'activities' 
              ? 'bg-amber-500 text-white' 
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <Activity size={20} />
          Activities ({unassignedActivities.length} unassigned)
        </button>
      </div>

      {/* Assignment Form */}
      <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Assign {activeTab}</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Client Select */}
          <div className="space-y-2">
            <label className="text-sm font-bold text-gray-600">Select Client</label>
            <select
              className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 outline-none"
              value={selectedClient}
              onChange={(e) => setSelectedClient(e.target.value)}
              disabled={loadingStates.clients}
            >
              <option value="">Choose a client...</option>
              {clients.map(client => (
                <option key={client.id} value={client.id}>
                  {client.company_name}
                </option>
              ))}
            </select>
          </div>

          {/* Item Select - FIXED */}
          <div className="space-y-2">
            <label className="text-sm font-bold text-gray-600">Select {activeTab}</label>
            <select
              className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 outline-none"
              value={selectedItem}
              onChange={(e) => setSelectedItem(e.target.value)}
              disabled={getLoadingState()}
            >
              <option value="">Choose {activeTab}...</option>
              {getUnassignedList().map(item => {
                if (!item) return null;
                return (
                  <option key={item.id} value={item.id}>
                    {activeTab === 'countries' && (item.country_name || 'Unknown')}
                    {activeTab === 'destinations' && `${item.destination_name || 'Unknown'} (${item.country_name || 'No Country'})`}
                    {activeTab === 'activities' && `${item.activity_name || 'Unknown'} - ${item.destination_name || 'No Destination'}`}
                  </option>
                );
              })}
            </select>
            {getUnassignedList().length === 0 && !getLoadingState() && (
              <p className="text-xs text-amber-600 mt-1">No unassigned {activeTab} available</p>
            )}
          </div>

          {/* Assign Button */}
          <div className="flex items-end">
            <button
              onClick={handleAssign}
              disabled={!selectedClient || !selectedItem || loading}
              className="w-full bg-gradient-to-r from-amber-500 to-orange-500 text-white py-3 rounded-xl font-bold hover:from-amber-600 hover:to-orange-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 className="animate-spin" size={20} /> : <Check size={20} />}
              {loading ? 'Assigning...' : 'Assign'}
            </button>
          </div>
        </div>

        <button
          onClick={loadAllData}
          disabled={loading}
          className="mt-4 text-amber-600 hover:text-amber-700 flex items-center gap-2 text-sm disabled:opacity-50"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          Refresh Data
        </button>
      </div>

      {/* Search and Toggle */}
      <div className="bg-white p-4 rounded-2xl shadow-sm mb-6">
        <div className="flex flex-col md:flex-row gap-4 items-center">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-3.5 text-gray-400" size={20} />
            <input
              type="text"
              placeholder={`Search ${activeTab}...`}
              className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 outline-none"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button
            onClick={() => setShowAssigned(!showAssigned)}
            className="flex items-center gap-2 px-4 py-3 bg-gray-100 rounded-xl text-gray-700 hover:bg-gray-200 transition-all"
          >
            {showAssigned ? <EyeOff size={20} /> : <Eye size={20} />}
            {showAssigned ? 'Hide' : 'Show'} Assigned
          </button>
        </div>
      </div>

      {/* Unassigned List */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden mb-8">
        <div className="p-4 border-b border-gray-100 bg-gray-50 flex justify-between items-center">
          <h3 className="font-bold text-gray-700">Unassigned {activeTab}</h3>
          <span className="text-sm text-gray-500">Total: {getUnassignedList().length}</span>
        </div>
        
        <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
          {getLoadingState() ? (
            <div className="p-8 text-center text-gray-500">
              <Loader2 className="animate-spin mx-auto mb-2" size={24} />
              Loading...
            </div>
          ) : filteredUnassigned.length > 0 ? (
            filteredUnassigned.map(item => (
              <div key={item.id} className="p-4 hover:bg-gray-50 flex justify-between items-center">
                <div>
                  <p className="font-medium">{renderItemName(item)}</p>
                  {renderItemDetails(item)}
                </div>
                <button
                  onClick={() => {
                    setSelectedItem(item.id);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="text-amber-600 hover:text-amber-700 text-sm font-medium px-3 py-1 rounded-lg hover:bg-amber-50 transition-colors"
                >
                  Select
                </button>
              </div>
            ))
          ) : (
            <div className="p-8 text-center text-gray-500">
              No unassigned {activeTab} found
            </div>
          )}
        </div>
      </div>

      {/* All Data with Client Info */}
      {showAssigned && getAllList().length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="p-4 border-b border-gray-100 bg-gray-50 flex justify-between items-center">
            <h3 className="font-bold text-gray-700">All {activeTab} with Client Info</h3>
            <span className="text-sm text-gray-500">Total: {getAllList().length}</span>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">Name</th>
                  {activeTab === 'destinations' && (
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">Country</th>
                  )}
                  {activeTab === 'activities' && (
                    <>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">Destination</th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">Category</th>
                    </>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">Assigned Client</th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {activeTab === 'countries' && filteredAll.map(item => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium">{item.country_name}</td>
                    <td className="px-6 py-4">{item.client_name || '—'}</td>
                    <td className="px-6 py-4">
                      {item.client_id ? (
                        <span className="bg-green-100 text-green-600 px-2 py-1 rounded-full text-xs font-bold">
                          Assigned
                        </span>
                      ) : (
                        <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-xs font-bold">
                          Unassigned
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
                {activeTab === 'destinations' && filteredAll.map(item => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium">{item.destination_name}</td>
                    <td className="px-6 py-4">{item.country_name}</td>
                    <td className="px-6 py-4">{item.client_name || '—'}</td>
                    <td className="px-6 py-4">
                      {item.client_id ? (
                        <span className="bg-green-100 text-green-600 px-2 py-1 rounded-full text-xs font-bold">
                          Assigned
                        </span>
                      ) : (
                        <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-xs font-bold">
                          Unassigned
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
                {activeTab === 'activities' && filteredAll.map(item => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium">{item.activity_name}</td>
                    <td className="px-6 py-4">{item.destination_name}</td>
                    <td className="px-6 py-4">{item.category_name}</td>
                    <td className="px-6 py-4">{item.client_name || '—'}</td>
                    <td className="px-6 py-4">
                      {item.client_id ? (
                        <span className="bg-green-100 text-green-600 px-2 py-1 rounded-full text-xs font-bold">
                          Assigned
                        </span>
                      ) : (
                        <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-xs font-bold">
                          Unassigned
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssignmentPanel;